import { useEffect, useRef, type ReactNode } from "react";
import { Link, useRouter } from "../lib/router";
import { isTouchDevice } from "../lib/device";
import { stashReceiptPhoto } from "../lib/receiptStash";

/**
 * A link to the add-record form that, on a phone, opens the photo choice (camera or photo library) inside
 * the tap itself — phones refuse it once the form has opened — and brings the photo along. Backing out of
 * the choice still opens the form, to type the receipt in. On a computer it is a plain link.
 */
export function ReceiptCameraLink({ to, className, children, enabled = true }: { to: string; className?: string; children: ReactNode; enabled?: boolean }) {
  const { navigate } = useRouter();
  const pick = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = pick.current;
    if (!input) return;
    // Without a photo the form opens to type in, and mustn't try to open the camera again.
    const cancelled = () => navigate(to.replace(/([?&])scan=1&?/, "$1").replace(/[?&]$/, ""));
    input.addEventListener("cancel", cancelled);
    return () => input.removeEventListener("cancel", cancelled);
  }, [navigate, to]);

  return (
    <>
      <Link
        to={to}
        className={className}
        onClick={(e) => {
          if (!enabled || !isTouchDevice() || !pick.current) return;
          e.preventDefault();
          pick.current.value = "";
          pick.current.click();
        }}
      >
        {children}
      </Link>
      <input
        ref={pick}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) stashReceiptPhoto(f);
          navigate(to);
        }}
      />
    </>
  );
}
